import React, { useContext, useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { useHistory } from 'react-router';

import { MDText } from 'i18n-react';

import { WarningIcon } from '@motorway/mw-highway-code';

import { getConfirmSaleStatus, postCOPCheck, updateBeneficiary } from 'PrivateAPI';
import { submitDocuments } from 'PrivateApis';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsDocumentCapture';
import { VERIFICATION_ANALYTICS_EVENTS } from 'Utilities/analytics/analyticsDocumentVerification';
import { stringBooleanToBoolean } from 'Utilities/formatter';
import { isApplicationNonProd } from 'Utilities/helpers';
import { captureException } from 'Utilities/sentry';
import { safeLocalStorage } from 'Utilities/storages';

import { NotificationBanner } from '../../../../components/notificationBanner/NotificationBanner';
import { SaleConfirmationSentryOpts } from '../DocumentCapture.helpers';
import LocalTexts from '../DocumentCapture.texts.json';
import { DocumentCaptureConfirmationProgressContext, DocumentCaptureConfirmationProgressContextType, DocumentCaptureOfferContext } from '../DocumentCaptureContexts';
import DocumentCaptureHeaderAndSummary from '../shared/DocumentCaptureHeaderAndSummary/DocumentCaptureHeaderAndSummary';
import { DocumentCaptureViewContainer } from '../shared/DocumentCaptureViewContainer/DocumentCaptureViewContainer';
import { StandaloneBankDetailsSentryOpts } from '../StandaloneBankDetails/StandaloneBankDetails.helpers';

import { ACCOUNT_HOLDER_NAME_FIELD_NAME } from './fields/AccountHolderName';
import { ACCOUNT_NUMBER_FIELD_NAME, ACCOUNT_NUMBER_MASKED_FORMAT } from './fields/AccountNumber';
import { ACCOUNT_TYPE_FIELD_NAME } from './fields/AccountType';
import { ACCOUNT_SORT_CODE_FIELD_NAME, ACCOUNT_SORT_CODE_MASKED_FORMAT } from './fields/SortCode';
import {
  ADD_BANK_DETAILS_ACTION_TYPE, BENEFICIARY_CREATION_RESPONSE_MESSAGES, COP_ADVANCED_WARNINGS,
  isFieldMasked, resolveBankDetailsNotification, resolveCopResponse,
  resolveSortCode, validateAccountName, validateAccountNumber, validateSortCode,
} from './AddBankDetails.helpers';
import { BeneficiaryErrors, BeneficiaryPayload, BeneficiaryValues, ContextValue } from './AddBankDetails.types';
import { AddBankDetailsContext } from './AddBankDetailsContext';
import { AddBankDetailsForm } from './AddBankDetailsForm';

import styles from './AddBankDetails.scss';

const LocalT = new MDText(LocalTexts.addBankDetails);

const AddBankDetailsFormProvider = ({ standaloneSubmission }: { standaloneSubmission: boolean }) => {
  const [copData, setCopData] = useState<Record<string, string>>({});
  const [contextValue, setContextValue] = useState<ContextValue>({} as ContextValue);
  const history = useHistory();

  const [generalError, setGeneralError] = useState<unknown>();

  const { valuation } = useContext(DocumentCaptureOfferContext);
  const reference = valuation?.valuation_ref;

  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const { vrm } = vehicle;

  const {
    setProgress,
  } = useContext<DocumentCaptureConfirmationProgressContextType>(DocumentCaptureConfirmationProgressContext);

  const documentCaptureConfirmationProgress = useContext(
    DocumentCaptureConfirmationProgressContext,
  );
  const { bankDetailsReviewFailed } = documentCaptureConfirmationProgress.progress;

  const copNotEnrolledActive = isApplicationNonProd && safeLocalStorage.getItem('copNotEnrolled');

  const sentryOpts = standaloneSubmission ? StandaloneBankDetailsSentryOpts : SaleConfirmationSentryOpts;
  const submitDocumentsHeaders = standaloneSubmission ? { 'x-mway-standalone-bank-details': '1' } : {};

  useEffect(() => {
    VERIFICATION_ANALYTICS_EVENTS.BANK_DETAILS_LOAD();
  }, []);

  const updateContext = (value: ContextValue) => {
    setContextValue((prevValue) => ({
      index: (prevValue.index ?? 0) + 1,
      ...value,
    }));
  };

  const generatePayload = (values: BeneficiaryValues) => {
    const {
      accountNumber, isBusiness, name, sortCode,
    } = values;

    const isAccountNumberMasked = isFieldMasked({ mask: ACCOUNT_NUMBER_MASKED_FORMAT, value: accountNumber });
    const isSortCodeMasked = isFieldMasked({ mask: ACCOUNT_SORT_CODE_MASKED_FORMAT, value: sortCode });

    let payload: BeneficiaryPayload = {} as BeneficiaryPayload;

    if (reference) {
      payload = {
        accountNumber: isAccountNumberMasked ? copData.accountNumber : accountNumber,
        isBusiness: stringBooleanToBoolean(isBusiness),
        name,
        reference,
        sortCode: resolveSortCode(isSortCodeMasked ? copData.sortCode : sortCode),
      };
    }

    // CoP 'NOT_ENROLLED' testing
    if (copNotEnrolledActive) {
      payload.simulateCode = 'NOT_ENROLLED';
    }

    return payload;
  };

  const createOrUpdateBeneficiary = async (values: BeneficiaryValues) => {
    const payload = generatePayload(values);
    try {
      // Note: This MUST be done before documents submission!
      await updateBeneficiary(payload);

      // although reference is used in createOrUpdateBeneficiary
      // this function will never reach before onSubmit function is called where reference is checked

      const referenceInt = reference && parseInt(reference) as number;

      // @ts-expect-error this needs to be changed to be type safe,
      // unlike the comment above it doesn't matter if this is unreachable, it shouldn't accept a falsy value
      await submitDocuments({ enquiryId: referenceInt, kind: 'bank_details' }, submitDocumentsHeaders);

      const updatedRequiredDocuments = await getConfirmSaleStatus(referenceInt);
      setProgress?.(updatedRequiredDocuments);

      if (!standaloneSubmission) {
        history.push(`/${vrm}/upload-documents`);
      }
    } catch (err: unknown) {
      const expectedErrors = Object.values(BENEFICIARY_CREATION_RESPONSE_MESSAGES);

      if (err instanceof Error && expectedErrors.includes(err?.message as typeof expectedErrors[1])) {
        updateContext(resolveCopResponse(err));
      } else {
        setGeneralError(err);
      }

      const errorObject = new Error(err as string);

      captureException(errorObject, sentryOpts);
    }
  };

  const onSubmit = async (values: BeneficiaryValues, form: HTMLFormElement) => {
    const payload = generatePayload(values);

    if (!reference) {
      const error = new Error('Reference not found');

      setGeneralError(error);
      captureException(error, sentryOpts, payload);

      return;
    }

    form.batch(() => {
      form.resetFieldState(ACCOUNT_HOLDER_NAME_FIELD_NAME);
      form.resetFieldState(ACCOUNT_NUMBER_FIELD_NAME);
      form.resetFieldState(ACCOUNT_SORT_CODE_FIELD_NAME);
      form.resetFieldState(ACCOUNT_TYPE_FIELD_NAME);
    });

    DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.DOCUMENTS_BANK_DETAILS_SUBMITTED({ label: `${standaloneSubmission ? 'bank_details_submitted_standalone' : 'bank_details_submitted'}` });

    VERIFICATION_ANALYTICS_EVENTS.BANK_DETAILS_CLICK();

    const copResult = await postCOPCheck(payload).catch((err) => {
      setGeneralError(err);

      captureException(err, sentryOpts, payload);
    });

    if (copResult) {
      const copResponse = resolveCopResponse(copResult);

      setCopData(copResult);
      updateContext({
        ...copResponse,
        resubmitAfterReviewFailed: true,
      });

      if (copResponse.action === ADD_BANK_DETAILS_ACTION_TYPE.PASS) {
        await createOrUpdateBeneficiary(values);
      }
    }
  };

  const accountNameValidator = validateAccountName();
  const accountNumberValidator = validateAccountNumber(LocalT.translate('fields.accountNumber.errorMessage') as string);
  const sortCodeValidator = validateSortCode(LocalT.translate('fields.sortCode.errorMessage') as string);

  const validate = (values: BeneficiaryValues) => {
    const { accountNumber, name, sortCode } = values;
    const errors: BeneficiaryErrors = {};

    const accountNameInvalid = accountNameValidator(name);
    const accountNumberInvalid = accountNumberValidator(accountNumber);
    const sortCodeInvalid = sortCodeValidator(sortCode);

    if (accountNameInvalid) {
      errors.name = accountNameInvalid;
    }

    if (accountNumberInvalid) {
      if (!isFieldMasked({ mask: ACCOUNT_NUMBER_MASKED_FORMAT, value: accountNumber })) {
        errors.accountNumber = accountNumberInvalid;
      }
    }

    if (sortCodeInvalid) {
      if (!isFieldMasked({ mask: ACCOUNT_SORT_CODE_MASKED_FORMAT, value: sortCode })) {
        errors.sortCode = sortCodeInvalid;
      }
    }

    return errors;
  };

  const advancedWarningErrors = Object.values(COP_ADVANCED_WARNINGS);
  /* for reentry only, check the not enrolled against the bank details reviewFailedFlag
      to ensure warning icon is not shown
  */

  const isAdvancedWarning = advancedWarningErrors.some((warning) => warning === copData?.message)
    || (bankDetailsReviewFailed && !contextValue?.message);

  const { errorLabel, errorMessage } = resolveBankDetailsNotification({
    generalError,
    message: contextValue?.message,
    reviewFailed: bankDetailsReviewFailed && !contextValue.resubmitAfterReviewFailed,
    standaloneSubmission,
  });

  const hasErrors = errorLabel && errorMessage;

  return (
    <AddBankDetailsContext.Provider value={contextValue}>
      <DocumentCaptureHeaderAndSummary
        header={LocalT.translate('pageTitle') as string}
        summary={(!hasErrors && !standaloneSubmission) && <p className={styles.text}>{LocalT.translate('pageText')}</p>}
      />
      <Form onSubmit={onSubmit as typeof createOrUpdateBeneficiary} validate={validate}>
        {({ handleSubmit }) => (
          <section className={styles.bankDetailsForm}>
            {hasErrors && (
              <NotificationBanner
                className={styles.error}
                icon={isAdvancedWarning || bankDetailsReviewFailed
                  ? <WarningIcon /> : null}
                label={errorLabel as string | null}
                message={ errorMessage }
                variant="error"
              />
            )}
            <AddBankDetailsForm {...{ handleSubmit }} onSubmitCallback={createOrUpdateBeneficiary} />
          </section>
        )}
      </Form>
    </AddBankDetailsContext.Provider>
  );
};

export const AddBankDetails = ({ standaloneSubmission = false, stepper = true }) => (
  <DocumentCaptureViewContainer showDealerVerification index={1} stepper={stepper}>
    <AddBankDetailsFormProvider standaloneSubmission={standaloneSubmission} />
  </DocumentCaptureViewContainer>
);
